import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';
import moment from 'moment';
import momentTz from 'moment-timezone';

import { removeTime2, calMultiDatesPrices } from '../helper';
import {
    SECTION_ORDER,
    initialUserInput,
    initialError,
    emptyAdditionalDate,
    getRequiredFields,
} from '../components/directorCamp/ProgramForm/fields';
import {
    isFilled,
    getValidationIssues,
    validateProgram,
    getMissingFields,
} from '../components/directorCamp/ProgramForm/validation';

/**
 * State for the program entry form.
 *
 * Ported from `Program Entry Updates/src/contexts/ProgramFormContext.tsx`, but holding the
 * app's own state shape: the `userInput` / `error` reducers lifted out of
 * `AddProgramModal.js:133-255` alongside the loose `useState` values that sit beside them
 * there (`byDates`, `additionalDates`, `cmQuestions`, `campId`, `district`, and so on).
 *
 * Two behaviours from the prototype are deliberately absent, per the decisions doc:
 * autosave (there is no draft concept in the API — it would POST half-built programs every
 * 20 seconds) and the publish toggle. The `beforeunload` guard is kept.
 */

const ProgramFormContext = createContext(null);

export const useProgramForm = () => {
    const ctx = useContext(ProgramFormContext);
    if (!ctx) {
        throw new Error('useProgramForm must be used within ProgramFormProvider');
    }
    return ctx;
};

const allOpenState = open =>
    SECTION_ORDER.reduce((acc, section) => ({ ...acc, [section.id]: open }), {});

const merge = (state, newState) => ({ ...state, ...newState });

/**
 * Each discount type in `sections/Extra.js` governs its own amount field. The amount input
 * declares `max={100}` while the type is Percentage (2), but rc-input-number does not
 * re-validate a value it is already holding — so switching an existing $500 over to
 * Percentage would leave 500% sitting in a box that would reject it on re-entry. The switch
 * is where the clamp has to happen, and this map is what makes one rule cover all three rows.
 */
const DISCOUNT_AMOUNT_KEY = {
    discountType: 'discount',
    siblingDiscountType: 'siblingDiscount',
    multiProgramDiscountType: 'multiProgramDiscount',
};

const PERCENTAGE = 2;

export const ProgramFormProvider = ({ children }) => {
    const [userInput, setUserInput] = useReducer(merge, null, initialUserInput);
    const [error, setError] = useReducer(merge, null, initialError);

    // Priced by program dates (1) or by registration windows (2). The old form's default.
    const [byDates, setByDates] = useState(2);
    const [additionalDates, setAdditionalDates] = useState(() => [emptyAdditionalDate()]);

    const [cmQuestions, setCmQuestions] = useState([]);
    const [cmQuestionsErr, setCmQuestionsErr] = useState('');
    const [dependsOnOptions, setDependsOnOptions] = useState([]);

    // Reference data fetched by the page shell.
    const [counselorList, setCounselorList] = useState([]);
    const [districtList, setDistrictList] = useState([]);
    const [district, setDistrict] = useState({});
    const [selectedSubCat, setSelectedSubCat] = useState([]);
    const [campId, setCampId] = useState('');

    const [disableOvernight, setDisableOvernight] = useState(0);

    // Hour of the chosen check-in, so the check-out picker can grey out earlier hours.
    const [tempTime, setTempTime] = useState('');

    // UI state.
    const [openSections, setOpenSections] = useState(() => allOpenState(true));
    const [requiredOnly, setRequiredOnly] = useState(false);
    const [btnLoder, setBtnLoder] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [dirty, setDirty] = useState(false);

    // Snapshot of the form as it last stood with no unsaved work. `null` means "adopt
    // whatever you see next as the new starting point". See the tracker near the bottom.
    const baseline = useRef(null);

    /*
     * The rich-text fields as the API last sent them, plus which of them a human has
     * actually typed in.
     *
     * react-quill does not hand back the HTML it was given. It parses the value into a
     * Delta and re-renders it, and that round trip is not order-stable for a node with
     * more than one inline style: `style="background-color: transparent; color: rgb(0, 0,
     * 255);"` comes back with the two declarations swapped, then swaps again on the next
     * load, for ever. react-quill then notices its own output differs from the prop and
     * fires `onChange` with `source: 'api'` — before the director has touched anything —
     * so form state ends up holding markup nobody wrote.
     *
     * Posting that re-serialisation made the API log an "About Program Changed" audit
     * entry on every save and rewrite the field with a value identical in every way that
     * renders. So at submit we send back exactly what we were given for any editor the
     * user never typed in. `resolveRichText` is where that decision is applied.
     */
    const pristineRichText = useRef({});
    const dirtyRichText = useRef(new Set());

    // ---- Change handlers -----------------------------------------------------
    // Ported from `AddProgramModal.js:530-682`. They live here rather than in each
    // section because every one of them writes to the same two reducers. The handlers
    // that also need Redux data or the Places geocoder stay in their own section.

    const handleChange = useCallback(e => {
        setUserInput({ [e.target.name]: e.target.value });
        setError({ [e.target.name]: '' });
    }, []);

    const handleSelect = useCallback(
        (e, name) => {
            // Changing the discount type can leave behind an amount that now exceeds the
            // price it discounts, so it is reset.
            if (name === 'discountType' && e.value && userInput.discount > userInput.registrationFee) {
                setUserInput({ discount: 0 });
            }

            // See `DISCOUNT_AMOUNT_KEY`. Only ever narrows a value, so a percentage that was
            // already in range is left exactly as the user typed it.
            const amountKey = DISCOUNT_AMOUNT_KEY[name];
            if (amountKey && e.value === PERCENTAGE && Number(userInput[amountKey]) > 100) {
                setUserInput({ [amountKey]: 100 });
            }

            setUserInput({ [name]: e.value });
            setError({ [name]: '' });
        },
        // `userInput` whole, rather than the four keys this reads: the clamp indexes it by a
        // key computed at call time, which no dependency list can name. It costs nothing —
        // the context `value` below is a fresh object every render anyway, so this callback's
        // identity was never what kept consumers from re-rendering.
        [userInput]
    );

    const handleNumberChange = useCallback(
        (e, name) => {
            if (name === 'discount') {
                if (userInput.discountType) {
                    if (e < userInput.registrationFee || e < calMultiDatesPrices(additionalDates)) {
                        setUserInput({ discount: e });
                        setError({ discount: '' });
                    } else {
                        setUserInput({ discount: 0 });
                        setError({
                            discount:
                                'Discount amount must be less than or equal to the standard price.',
                        });
                    }
                }
                return;
            }

            setUserInput({ [name]: e });
            setError({ [name]: '' });
        },
        [userInput.discountType, userInput.registrationFee, additionalDates]
    );

    // Program start/end come from a single range picker. Both ends are stored as midnight
    // in the program's own timezone, plus a formatted copy the API expects.
    const handleDateRangeChange = useCallback(
        (e, from, to) => {
            const tz = userInput.programTimezone;
            const toTzMidnightUtc = momentDate =>
                momentTz.tz(momentDate.format('YYYY-MM-DD'), tz).valueOf();
            const toFormatted = momentDate =>
                `${momentDate.format('YYYY-MM-DD')}T00:00:00.000Z`;

            setUserInput({
                [from]: toTzMidnightUtc(e[0]),
                [to]: toTzMidnightUtc(e[1]),
                startDateFormatted: toFormatted(e[0]),
                endDateFormatted: toFormatted(e[1]),
            });
            setError({ [from]: '', [to]: '' });
        },
        [userInput.programTimezone]
    );

    const handleMultiSelect = useCallback((e, name) => {
        setUserInput({ [name]: e ? e.map(i => i.value) : '' });
        setError({ [name]: '' });
    }, []);

    /*
     * `source` is react-quill's own: 'user' when a human typed, 'api' when the editor
     * re-rendered a value it was handed. Only the former marks the field edited.
     *
     * It defaults to 'user' on purpose. A caller that forgets to pass it then behaves
     * exactly as this form always has — the field counts as edited and is posted. The
     * opposite default would make a forgotten caller silently drop the director's work,
     * which is far worse than the phantom audit entry this exists to prevent.
     *
     * State is still written unconditionally, so the editor stays a controlled component
     * and react-quill never re-seeds itself mid-edit.
     */
    const handleEditerChange = useCallback((value, name, source = 'user') => {
        if (source === 'user') dirtyRichText.current.add(name);
        setUserInput({ [name]: value });
        setError({ [name]: '' });
    }, []);

    /**
     * What to put on the wire for one rich-text field: the untouched original when the
     * user never typed in it, otherwise what they wrote.
     */
    const resolveRichText = useCallback((name, value) => {
        if (dirtyRichText.current.has(name)) return value || '';
        const pristine = pristineRichText.current[name];
        return pristine !== undefined ? pristine : (value || '');
    }, []);

    /** Records the values the API just sent, so an untouched editor can post them back. */
    const seedRichText = useCallback(fields => {
        pristineRichText.current = { ...fields };
        dirtyRichText.current = new Set();
    }, []);

    const handleProgramTimeZoneChange = useCallback(e => {
        setUserInput({ programTimezone: e.value });
        setError({ programTimezone: '' });
    }, []);

    const handleDateChange = useCallback((e, key) => {
        setUserInput({ [key]: removeTime2(new Date(e._d)) });

        // Closing Early Bird opens Standard the next day, and invalidates its close date.
        if (key === 'dateEarlyRegisterClosed') {
            setUserInput({
                registerOpenDate: removeTime2(new Date(e._d)) + 86400000,
                registerCloseDate: '',
            });
            setError({ registerOpenDate: '' });
        }

        // Closing Standard opens Late the next day.
        if (key === 'registerCloseDate') {
            setUserInput({ dateLateRegisterOpen: removeTime2(new Date(e._d)) + 86400000 });
            setError({ dateLateRegisterOpen: '' });
        }

        setError({ [key]: '' });
    }, []);

    const handleTimeChange = useCallback(
        (e, name) => {
            if (name === 'checkInTime') {
                setTempTime(new Date(e._d).getHours());
                // A new check-in can invalidate the existing check-out.
                if (userInput.checkOutTime) setUserInput({ checkOutTime: '' });
            }
            setUserInput({ [name]: moment(e._d).format('h:mm a') });
            setError({ [name]: '' });
        },
        [userInput.checkOutTime]
    );

    const handleCheckboxChange = useCallback(e => {
        setUserInput({ isOnline: e.target.checked ? 1 : 0 });
        if (e.target.checked) {
            setUserInput({
                addressLine1: '',
                city: '',
                state: '',
                zipCode: '',
                lat: '',
                long: '',
                location: '',
            });
        }
    }, []);

    // ---- Sections ------------------------------------------------------------
    const toggleSection = useCallback(id => {
        setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
    }, []);

    const openSection = useCallback(id => {
        setOpenSections(prev => (prev[id] ? prev : { ...prev, [id]: true }));
    }, []);

    const setAllSections = useCallback(open => {
        setOpenSections(allOpenState(open));
    }, []);

    const allExpanded = SECTION_ORDER.every(({ id }) => openSections[id]);

    // ---- Program date rows ---------------------------------------------------
    const addDateRow = useCallback(
        () => setAdditionalDates(prev => [...prev, emptyAdditionalDate()]),
        []
    );

    const updateDateRow = useCallback(
        (index, patch) =>
            setAdditionalDates(prev =>
                prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
            ),
        []
    );

    const removeDateRow = useCallback(
        index => setAdditionalDates(prev => prev.filter((row, i) => i !== index)),
        []
    );

    // ---- Progress ------------------------------------------------------------
    const requiredBySection = useMemo(
        () => getRequiredFields(userInput, byDates),
        [userInput, byDates]
    );

    const progressBySection = useMemo(() => {
        const result = {};
        SECTION_ORDER.forEach(({ id }) => {
            const required = requiredBySection[id];
            const filled = required.filter(key => isFilled(userInput[key])).length;
            result[id] = { filled, total: required.length, complete: filled === required.length };
        });
        return result;
    }, [userInput, requiredBySection]);

    const overall = useMemo(() => {
        const totals = Object.keys(progressBySection).reduce(
            (acc, id) => ({
                filled: acc.filled + progressBySection[id].filled,
                total: acc.total + progressBySection[id].total,
            }),
            { filled: 0, total: 0 }
        );
        return {
            ...totals,
            percent: totals.total === 0 ? 0 : Math.round((totals.filled / totals.total) * 100),
        };
    }, [progressBySection]);

    // ---- Issues --------------------------------------------------------------
    const issues = useMemo(
        () => getValidationIssues(userInput, additionalDates, byDates),
        [userInput, additionalDates, byDates]
    );

    const issuesBySection = useMemo(() => {
        const result = SECTION_ORDER.reduce((acc, section) => ({ ...acc, [section.id]: [] }), {});
        issues.forEach(issue => result[issue.section].push(issue));
        return result;
    }, [issues]);

    /**
     * Presentation state for one field: whether to paint it as invalid, and the single
     * most relevant note to show beneath it. A blocking error outranks an advisory one.
     */
    const fieldStatus = useCallback(
        key => {
            const matches = issues.filter(issue => issue.field === key);
            const issue = matches.find(i => i.severity === 'error') || matches[0];
            const message = error[key];

            if (message) {
                return { error: true, issue: { severity: 'error', message } };
            }
            return { error: issue ? issue.severity === 'error' : false, issue };
        },
        [error, issues]
    );

    const rowIssue = useCallback(
        (rowIndex, rowField) =>
            issues.find(issue => issue.rowIndex === rowIndex && issue.rowField === rowField),
        [issues]
    );

    const missingFields = useMemo(
        () => getMissingFields(userInput, byDates),
        [userInput, byDates]
    );

    /**
     * Runs the blocking rules and writes the results into the error reducer.
     * Returns the same `{ valid, missing }` the caller needs to decide whether to submit.
     */
    const validate = useCallback(() => {
        const { valid, errors, rows, questionsError } = validateProgram({
            userInput,
            byDates,
            additionalDates,
            cmQuestions,
        });

        // Reset first so cleared fields stop showing yesterday's message.
        setError({ ...initialError(), ...errors });
        setCmQuestionsErr(questionsError);
        if (byDates === 1) setAdditionalDates(rows);
        setSubmitAttempted(true);

        return { valid, missing: getMissingFields(userInput, byDates) };
    }, [userInput, byDates, additionalDates, cmQuestions]);

    /**
     * Drops the field-level message as soon as the user edits that field, matching the
     * old form, which cleared errors inside each change handler.
     */
    const clearFieldError = useCallback(
        key => setError({ [key]: '' }),
        []
    );

    /**
     * Declares the current values to be the starting point, not an edit.
     *
     * Call it alongside any programmatic write — seeding a new program, hydrating an
     * existing one — so closing the form straight afterwards does not warn about changes
     * the user never made.
     */
    const markPristine = useCallback(() => {
        baseline.current = null;
        setDirty(false);
    }, []);

    const resetForm = useCallback(() => {
        setUserInput(initialUserInput());
        setError(initialError());
        setAdditionalDates([emptyAdditionalDate()]);
        setCmQuestions([]);
        setCmQuestionsErr('');
        setByDates(2);
        setDisableOvernight(0);
        setSelectedSubCat([]);
        setDistrict({});
        setSubmitAttempted(false);
        baseline.current = null;
        setDirty(false);
        // Belt and braces. Today the provider unmounts with the modal
        // (`modals/index.js:45` renders it only while `dProgramModal` is set), so these
        // refs cannot outlive one program. If that ever changes, a stale pristine value
        // would be posted as if it belonged to the next program opened.
        pristineRichText.current = {};
        dirtyRichText.current = new Set();
    }, []);

    // The pool a conditional question can point at is just the other questions
    // (`AddProgramModal.js:421-425`).
    useEffect(() => {
        setDependsOnOptions(cmQuestions.map(ques => ({ label: ques.title, value: ques?.questionId })));
    }, [cmQuestions]);

    // ---- Unsaved-changes tracking -------------------------------------------
    /**
     * Compares against a baseline rather than counting renders.
     *
     * Two earlier attempts failed on this. Skipping the first render missed the fact that
     * both entry paths write after mount — a create seeds its program code, an edit hydrates
     * from the API. Replacing that with a skip-once flag then failed because the app runs
     * under `React.StrictMode` (`src/index.js:26`), which invokes every effect twice on
     * mount while refs survive in between: the first pass consumed the flag and the second
     * marked the untouched form dirty.
     *
     * A snapshot has neither problem. Re-running with unchanged state is a no-op however
     * many times it happens, so the double invocation is harmless, and `markPristine()`
     * clears the baseline so the next commit — however far in the future the API is —
     * becomes the new starting point. Editing a value back to what it was correctly counts
     * as no longer dirty.
     */
    useEffect(() => {
        const current = JSON.stringify({ userInput, additionalDates, cmQuestions, byDates });

        if (baseline.current === null) {
            baseline.current = current;
            return;
        }

        setDirty(current !== baseline.current);
    }, [userInput, additionalDates, cmQuestions, byDates]);

    useEffect(() => {
        if (!dirty) return undefined;
        const handler = event => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirty]);

    const value = {
        userInput,
        setUserInput,
        error,
        setError,
        clearFieldError,

        handleChange,
        handleSelect,
        handleMultiSelect,
        handleNumberChange,
        handleEditerChange,
        resolveRichText,
        seedRichText,
        handleDateChange,
        handleDateRangeChange,
        handleTimeChange,
        handleProgramTimeZoneChange,
        handleCheckboxChange,
        tempTime,
        setTempTime,

        byDates,
        setByDates,
        additionalDates,
        setAdditionalDates,
        addDateRow,
        updateDateRow,
        removeDateRow,

        cmQuestions,
        setCmQuestions,
        cmQuestionsErr,
        setCmQuestionsErr,
        dependsOnOptions,
        setDependsOnOptions,

        counselorList,
        setCounselorList,
        districtList,
        setDistrictList,
        district,
        setDistrict,
        selectedSubCat,
        setSelectedSubCat,
        campId,
        setCampId,
        disableOvernight,
        setDisableOvernight,

        openSections,
        toggleSection,
        openSection,
        setAllSections,
        allExpanded,
        requiredOnly,
        setRequiredOnly,

        progressBySection,
        overall,
        requiredBySection,
        issues,
        issuesBySection,
        fieldStatus,
        rowIssue,
        missingFields,

        validate,
        resetForm,
        submitAttempted,
        btnLoder,
        setBtnLoder,
        dirty,
        setDirty,
        markPristine,
    };

    return (
        <ProgramFormContext.Provider value={value}>
            {children}
        </ProgramFormContext.Provider>
    );
};
